import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router'
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import {
  mdiAlert,
  mdiChevronDown,
  mdiChevronRight,
  mdiHistory,
  mdiLoading,
  mdiShapePlus,
} from '@mdi/js'
import { $api } from '../api/queryClient'
import { apiClient } from '../api/client'
import { useBreadcrumbs } from '../hooks/useBreadcrumbs'
import { jobErrorMessage, useElapsedSeconds, useJobPoll } from './plan-editor/useJobPoll'
import { useCan } from '../contexts/PermissionsContext'
import { useLocale } from '../contexts/LocaleContext'
import { useFormatters } from '../lib/format'
import { SECTION_ICONS } from '../lib/section-icons'
import { cn } from '../lib/utils'
import {
  allNewIds,
  defaultFamily,
  familyCheckState,
  indexDiff,
  isEmptyTarget,
  isSelectable,
  keepEligible,
  localizedLabel,
  matchesQuery,
  parseRefId,
  parseStatuses,
  reextractionDocuments,
  isTuned,
  refId,
  selectableIds,
  selectItems,
  selectionPayload,
  sourceAsChanges,
  SYNC_STATUSES,
  unselectItems,
  type CheckState,
  type Selection,
  type SyncDiff,
  type SyncDiffItem,
  type SyncFamily,
  type SyncFamilyDiff,
  type SyncMissingDependency,
  type SyncOutcome,
  type SyncSourceKind,
  type SyncStatus,
} from '../lib/ontology-sync'
import { PageHeader } from './shared/PageHeader'
import { FieldDiff } from './ontology/FieldDiff'
import { Button } from './ui/button'
import { Badge } from './ui/badge'
import { Alert, AlertDescription, AlertTitle } from './ui/alert'
import { AppIcon } from './ui/icon'
import { Input } from './ui/input'
import { Checkbox } from './ui/checkbox'
import { EmptyState } from './ui/empty-state'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select'
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog'

const STATUS_BADGE: Record<SyncStatus, 'success' | 'warning' | 'secondary' | 'outline' | 'destructive'> = {
  new: 'success',
  different: 'warning',
  identical: 'secondary',
  local_only: 'outline',
  unsyncable: 'destructive',
}

/** The body of a sync error (409 / 422). */
interface SyncErrorBody {
  title?: string
  code?: string
  stale?: Array<{ family: string; key: string }>
  missing?: SyncMissingDependency[]
  errors?: Array<{ code?: string; message?: string }>
}

type ReviewState =
  | { phase: 'closed' }
  | { phase: 'loading'; count: number }
  | { phase: 'ready'; count: number; outcome: SyncOutcome }
  | { phase: 'error'; count: number; error: SyncErrorBody }

/**
 * TypesSyncPage compares the organization's ontology with its template (or
 * the platform defaults) and applies what the admin picks (spec
 * 2026-09-25-02). Browsing reads the server's fast diff; *Review* rehearses
 * the selection for real in a rolled-back transaction, and *Apply* runs the
 * same function and commits.
 */
export function TypesSyncPage() {
  const { org } = useParams({ from: '/$org/types/sync/' })
  const search = useSearch({ from: '/$org/types/sync/' })
  const navigate = useNavigate({ from: '/$org/types/sync/' })
  const { t } = useTranslation('types')
  const { locale } = useLocale()
  const { formatDate } = useFormatters()
  const canWrite = useCan('configuration.write')
  const queryClient = useQueryClient()

  useBreadcrumbs([
    { title: t('breadcrumb.home'), path: `/${org}` },
    { title: t('breadcrumb.settings'), path: `/${org}/settings` },
    { title: t('breadcrumb.types'), path: `/${org}/types` },
    { title: t('sync.breadcrumb'), path: `/${org}/types/sync` },
  ])

  const diffQuery = $api.useQuery('get', '/api/v1/organizations/{orgCode}/types/sync/diff', {
    params: { path: { orgCode: org }, query: search.source ? { source: search.source } : {} },
  })
  const diff: SyncDiff | undefined = diffQuery.data?.data
  const loadError = diffQuery.error as SyncErrorBody | null
  const index = useMemo(() => indexDiff(diff), [diff])

  const sourceKind: SyncSourceKind = (diff?.source.kind as SyncSourceKind | undefined) ?? search.source ?? 'template'
  const templateName = diff?.template?.name ?? diff?.source.organization?.name ?? ''
  const sourceName = sourceKind === 'template' ? templateName : t('sync.source_defaults')

  const updateSearch = useCallback(
    (patch: Partial<typeof search>) => {
      navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true, resetScroll: false })
    },
    [navigate],
  )

  // Selection is component state, cleared when the source changes.
  const [selection, setSelection] = useState<Selection>(() => new Map())
  // Tuned compliance rules brought back to their shipped values.
  const [resetTuning, setResetTuning] = useState<Set<string>>(() => new Set())
  useEffect(() => {
    setSelection(new Map())
    setResetTuning(new Set())
  }, [sourceKind, org])
  const tuningContext = useMemo(() => ({
    reset: resetTuning,
    toggle: (id: string, on: boolean) => setResetTuning((prev) => {
      const next = new Set(prev)
      if (on) next.add(id)
      else next.delete(id)
      return next
    }),
  }), [resetTuning])

  const [alsoUnchecked, setAlsoUnchecked] = useState<{ count: number; label: string } | null>(null)
  const [staleNotice, setStaleNotice] = useState(false)
  const [review, setReview] = useState<ReviewState>({ phase: 'closed' })
  const [applying, setApplying] = useState(false)
  const [applyError, setApplyError] = useState<string | null>(null)
  // The apply is a job (spec 2026-09-30-06): submit, then follow it to a final status.
  const [applyJobUid, setApplyJobUid] = useState<string | null>(null)
  const [applyStartedAt, setApplyStartedAt] = useState<number | null>(null)
  const applySeconds = useElapsedSeconds(applyStartedAt)
  const applyPoll = useJobPoll(org, applyJobUid, { intervalMs: 1500, firstCheckMs: 500 })
  // Re-run the meta extraction of the documents whose type the sync changes
  // on an extraction key (spec 2026-10-14-05). Offered when the preview lists
  // some, on by default.
  const [reextract, setReextract] = useState(true)

  const labelOf = useCallback(
    (id: string) => {
      const entry = index.items.get(id)
      return entry ? localizedLabel(entry.item.label, locale) || entry.item.key : parseRefId(id).key
    },
    [index, locale],
  )

  const toggle = useCallback(
    (ids: string[], checked: boolean) => {
      setAlsoUnchecked(null)
      if (checked) {
        setSelection(selectItems(selection, ids, index))
        return
      }
      const { selection: next, alsoUnchecked: others } = unselectItems(selection, ids, index)
      setSelection(next)
      if (others.length > 0) setAlsoUnchecked({ count: others.length, label: labelOf(ids[0]) })
    },
    [selection, index, labelOf],
  )

  const requiredCount = useMemo(() => [...selection.values()].filter((v) => v !== null).length, [selection])

  const family = defaultFamily(diff, search.family)
  const statuses = useMemo(() => parseStatuses(search.status), [search.status])
  const familyDiff = diff?.families.find((f) => f.family === family)

  // A 409: the page reloads the diff and keeps the still-eligible keys.
  const reloadAfterStale = useCallback(
    async (current: Selection) => {
      setReview({ phase: 'closed' })
      setStaleNotice(true)
      const res = await diffQuery.refetch()
      const fresh = indexDiff(res.data?.data)
      setSelection(keepEligible(current, fresh))
    },
    [diffQuery],
  )

  const startReview = useCallback(
    async (sel: Selection) => {
      const items = selectionPayload(sel, index, resetTuning)
      if (items.length === 0) return
      setStaleNotice(false)
      setApplyError(null)
      setReview({ phase: 'loading', count: items.length })
      const { data, error, response } = await apiClient.POST('/api/v1/organizations/{orgCode}/types/sync/preview', {
        params: { path: { orgCode: org } },
        body: { source: sourceKind, items },
      })
      if (response.status === 409) {
        await reloadAfterStale(sel)
        return
      }
      if (error || !data) {
        setReview({ phase: 'error', count: items.length, error: (error as SyncErrorBody) ?? {} })
        return
      }
      setReextract(true)
      setReview({ phase: 'ready', count: items.length, outcome: data.data })
    },
    [index, org, sourceKind, reloadAfterStale, resetTuning],
  )

  const apply = useCallback(async () => {
    const items = selectionPayload(selection, index, resetTuning)
    const withReextraction =
      reextract && review.phase === 'ready' && reextractionDocuments(review.outcome.reextraction) > 0
    setApplying(true)
    setApplyError(null)
    try {
      const { data, error } = await apiClient.POST('/api/v1/organizations/{orgCode}/types/sync/runs', {
        params: { path: { orgCode: org } },
        body: { source: sourceKind, items, ...(withReextraction ? { reextract: true } : {}) },
      })
      if (error || !data) {
        setApplyError((error as SyncErrorBody)?.title ?? t('sync.review.apply_error'))
        setApplying(false)
        return
      }
      setApplyStartedAt(Date.now())
      setApplyJobUid(data.data.job_uid)
    } catch {
      setApplyError(t('sync.review.apply_error'))
      setApplying(false)
    }
  }, [selection, index, org, sourceKind, t, reextract, review, resetTuning])

  // The job settled: go to the run, or report why it failed.
  const settledJobRef = useRef<string | null>(null)
  useEffect(() => {
    const finalStatus = applyPoll.finalStatus
    if (!applyJobUid || !finalStatus || settledJobRef.current === applyJobUid) return
    settledJobRef.current = applyJobUid
    const output = applyPoll.execution?.output as
      | { run_uid?: string; error?: { status?: number; body?: SyncErrorBody } }
      | undefined
    setApplyJobUid(null)
    setApplyStartedAt(null)
    if (finalStatus === 'success' && output?.run_uid) {
      invalidateOntologyQueries(queryClient, org)
      setApplying(false)
      setReview({ phase: 'closed' })
      navigate({ to: '/$org/types/sync/runs/$runUid', params: { org, runUid: output.run_uid } })
      return
    }
    const failure = output?.error
    if (failure?.status === 409 || failure?.body?.code === 'ONTOLOGY_SYNC_STALE') {
      setApplying(false)
      void reloadAfterStale(selection)
      return
    }
    setApplyError(failure?.body?.title ?? (applyPoll.execution?.error ? jobErrorMessage(applyPoll.execution.error) : t('sync.review.apply_error')))
    setApplying(false)
  }, [applyJobUid, applyPoll.finalStatus, applyPoll.execution, selection, org, queryClient, navigate, reloadAfterStale, t])

  const reviewAll = useCallback(() => {
    const sel = selectItems(new Map(), allNewIds(diff), index)
    setSelection(sel)
    void startReview(sel)
  }, [diff, index, startReview])

  const totals = diff?.totals ?? {}
  const emptyTarget = isEmptyTarget(diff)

  return (
    <TuningContext.Provider value={tuningContext}>
    <div className="space-y-4">
      <PageHeader
        data-testid="ontology-sync-header"
        icon={SECTION_ICONS.types}
        title={t('sync.title')}
        description={
          sourceKind === 'template' && templateName
            ? t('sync.description_template', { name: templateName })
            : t('sync.description_defaults')
        }
      />

      <SourceStrip
        org={org}
        sourceKind={sourceKind}
        diff={diff}
        onSource={(source) => updateSearch({ source, family: undefined })}
        formatDate={(d) => formatDate(d, { dateStyle: 'medium', timeStyle: 'short' })}
      />

      {staleNotice && (
        <Alert variant="warning" data-testid="ontology-sync-stale-notice">
          <AppIcon path={mdiAlert} size="md" />
          <AlertTitle>{t('sync.stale')}</AlertTitle>
          <AlertDescription className="flex flex-wrap items-center justify-between gap-2">
            <span>{t('sync.stale_detail')}</span>
            <Button variant="ghost" size="sm" onClick={() => setStaleNotice(false)} data-testid="ontology-sync-stale-dismiss">
              {t('sync.dismiss')}
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {alsoUnchecked && (
        <Alert data-testid="ontology-sync-unchecked-notice">
          <AlertDescription className="flex flex-wrap items-center justify-between gap-2">
            <span>{t('sync.also_unchecked', { count: alsoUnchecked.count, label: alsoUnchecked.label })}</span>
            <Button variant="ghost" size="sm" onClick={() => setAlsoUnchecked(null)} data-testid="ontology-sync-unchecked-dismiss">
              {t('sync.dismiss')}
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {diffQuery.isLoading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="ontology-sync-loading">
          <AppIcon path={mdiLoading} size="md" className="motion-safe:animate-spin" />
          {t('sync.loading')}
        </div>
      )}

      {loadError && !diffQuery.isLoading && (
        <Alert variant="destructive" data-testid="ontology-sync-load-error">
          <AppIcon path={mdiAlert} size="md" />
          <AlertTitle>{t('sync.load_error')}</AlertTitle>
          {loadError.code === 'ONTOLOGY_SYNC_NO_TEMPLATE' && (
            <AlertDescription className="flex flex-wrap items-center gap-2">
              <span>{t('sync.no_template_error')}</span>
              <Button size="sm" variant="outline" onClick={() => updateSearch({ source: 'defaults' })} data-testid="ontology-sync-use-defaults">
                {t('sync.source_defaults')}
              </Button>
            </AlertDescription>
          )}
        </Alert>
      )}

      {diff && (
        <>
          <div
            className="sticky top-0 z-10 flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-background/95 p-3 backdrop-blur supports-[backdrop-filter]:bg-background/80"
            data-testid="ontology-sync-summary"
          >
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
              {SYNC_STATUSES.filter((s) => s !== 'unsyncable' || (totals.unsyncable ?? 0) > 0).map((s) => (
                <span
                  key={s}
                  data-testid={`ontology-sync-total-${s}`}
                  data-count={totals[s] ?? 0}
                  className={cn(
                    s === 'new' && 'text-success',
                    s === 'different' && 'text-warning',
                    (s === 'identical' || s === 'local_only') && 'text-muted-foreground',
                    s === 'unsyncable' && 'text-destructive',
                  )}
                >
                  {t(`sync.total.${s}`, { count: totals[s] ?? 0 })}
                </span>
              ))}
              {canWrite && (
                <span className="font-medium" data-testid="ontology-sync-selected-count" data-count={selection.size}>
                  {requiredCount > 0
                    ? t('sync.selected_required', { count: selection.size - requiredCount, required: requiredCount })
                    : t('sync.selected', { count: selection.size })}
                </span>
              )}
            </div>
            {canWrite && (
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => toggle(allNewIds(diff), true)}
                  disabled={(totals.new ?? 0) === 0}
                  data-testid="ontology-sync-select-all-new"
                >
                  {t('sync.select_all_new')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setSelection(new Map())
                    setAlsoUnchecked(null)
                  }}
                  disabled={selection.size === 0}
                  data-testid="ontology-sync-clear"
                >
                  {t('sync.clear')}
                </Button>
                <Button
                  size="sm"
                  onClick={() => void startReview(selection)}
                  disabled={selection.size === 0 || review.phase === 'loading'}
                  data-testid="ontology-sync-review-btn"
                >
                  {t('sync.review', { count: selection.size })}
                </Button>
              </div>
            )}
          </div>

          {emptyTarget && (
            <EmptyState
              variant="panel"
              tone="solid"
              icon={mdiShapePlus}
              title={t('sync.empty_title')}
              description={
                sourceKind === 'template'
                  ? t('sync.empty_description_template', {
                      name: templateName,
                      items: t('sync.empty_count', { count: totals.new ?? 0 }),
                    })
                  : t('sync.empty_description_defaults', { items: t('sync.empty_count', { count: totals.new ?? 0 }) })
              }
              action={
                canWrite ? (
                  <Button size="sm" onClick={reviewAll} data-testid="ontology-sync-review-all">
                    {t('sync.review_all')}
                  </Button>
                ) : undefined
              }
              data-testid="ontology-sync-empty-panel"
            />
          )}

          <div className="grid gap-4 lg:grid-cols-[18rem_minmax(0,1fr)]">
            <FamilyNav
              families={diff.families}
              current={family}
              selection={selection}
              canWrite={canWrite}
              onFamily={(f) => updateSearch({ family: f, q: undefined })}
              onToggleFamily={(f, checked) => toggle(selectableIds(f), checked)}
            />
            {familyDiff && (
              <FamilyPanel
                familyDiff={familyDiff}
                sourceKind={sourceKind}
                statuses={statuses}
                q={search.q ?? ''}
                selection={selection}
                canWrite={canWrite}
                locale={locale}
                onQuery={(q) => updateSearch({ q: q || undefined })}
                onStatuses={(next) =>
                  updateSearch({ status: next.length === SYNC_STATUSES.length ? undefined : next.join(',') })
                }
                onToggle={(id, checked) => toggle([id], checked)}
                labelOf={labelOf}
              />
            )}
          </div>
        </>
      )}

      <ReviewDialog
        review={review}
        sourceKind={sourceKind}
        sourceName={sourceName}
        applying={applying}
        applySeconds={applySeconds}
        applyError={applyError}
        locale={locale}
        reextract={reextract}
        onReextractChange={setReextract}
        onCancel={() => setReview({ phase: 'closed' })}
        onApply={() => void apply()}
      />
    </div>
    </TuningContext.Provider>
  )
}

/** The tuned compliance rules the admin resets to their shipped values. */
const TuningContext = createContext<{ reset: ReadonlySet<string>; toggle: (id: string, on: boolean) => void }>({
  reset: new Set(),
  toggle: () => {},
})

function SourceStrip({
  org,
  sourceKind,
  diff,
  onSource,
  formatDate,
}: {
  org: string
  sourceKind: SyncSourceKind
  diff: SyncDiff | undefined
  onSource: (s: SyncSourceKind) => void
  formatDate: (d: string) => string
}) {
  const { t } = useTranslation('types')
  const template = diff?.template
  const lastRun = diff?.last_run
  return (
    <div className="flex flex-wrap items-center justify-between gap-3" data-testid="ontology-sync-source-strip">
      <div role="group" aria-label={t('sync.source_aria')} className="flex min-w-0 max-w-full rounded-md border p-0.5">
        <Button
          variant={sourceKind === 'template' ? 'secondary' : 'ghost'}
          size="sm"
          aria-pressed={sourceKind === 'template'}
          disabled={diff !== undefined && !template}
          title={diff !== undefined && !template ? t('sync.source_template_disabled') : undefined}
          onClick={() => onSource('template')}
          className="min-w-0 shrink"
          data-testid="ontology-sync-source-template"
        >
          <span className="min-w-0 truncate">
            {template ? t('sync.source_template', { name: template.name }) : t('sync.source_template_none')}
          </span>
        </Button>
        <Button
          variant={sourceKind === 'defaults' ? 'secondary' : 'ghost'}
          size="sm"
          aria-pressed={sourceKind === 'defaults'}
          onClick={() => onSource('defaults')}
          className="min-w-0 shrink-0"
          data-testid="ontology-sync-source-defaults"
        >
          <span className="truncate">{t('sync.source_defaults')}</span>
        </Button>
      </div>
      <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
        {lastRun && (
          <Link
            to="/$org/types/sync/runs/$runUid"
            params={{ org, runUid: lastRun.uid }}
            className="hover:underline"
            data-testid="ontology-sync-last-run"
          >
            {t('sync.last_sync', {
              date: formatDate(lastRun.finished_at),
              user: lastRun.actor?.name || lastRun.actor?.email || t('sync.unknown_user'),
              count: lastRun.counts.created + lastRun.counts.updated,
            })}
          </Link>
        )}
        <Link
          to="/$org/types/sync/runs"
          params={{ org }}
          className="inline-flex items-center gap-1 text-primary hover:underline"
          data-testid="ontology-sync-history-link"
        >
          <AppIcon path={mdiHistory} size="sm" />
          {t('sync.history')}
        </Link>
      </div>
    </div>
  )
}

function TriStateCheckbox({
  state,
  disabled,
  onChange,
  label,
  testId,
}: {
  state: CheckState
  disabled?: boolean
  onChange: (checked: boolean) => void
  label: string
  testId: string
}) {
  const ref = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = state === 'indeterminate'
  }, [state])
  return (
    <input
      ref={ref}
      type="checkbox"
      className="h-4 w-4 rounded border border-border text-primary focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
      checked={state === 'checked'}
      aria-checked={state === 'indeterminate' ? 'mixed' : state === 'checked'}
      disabled={disabled}
      onChange={(e) => onChange(state === 'indeterminate' ? true : e.target.checked)}
      aria-label={label}
      title={label}
      data-testid={testId}
      data-state={state}
    />
  )
}

function FamilyNav({
  families,
  current,
  selection,
  canWrite,
  onFamily,
  onToggleFamily,
}: {
  families: SyncFamilyDiff[]
  current: SyncFamily
  selection: Selection
  canWrite: boolean
  onFamily: (f: SyncFamily) => void
  onToggleFamily: (f: SyncFamilyDiff, checked: boolean) => void
}) {
  const { t } = useTranslation('types')
  return (
    <>
      {/* Below lg the list is a Select above the panel. */}
      <div className="lg:hidden">
        <Select value={current} onValueChange={(v) => onFamily(v as SyncFamily)}>
          <SelectTrigger aria-label={t('sync.family_select')} data-testid="ontology-sync-family-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {families.map((f) => (
              <SelectItem key={f.family} value={f.family} data-testid={`ontology-sync-family-option-${f.family}`}>
                {t(`sync.families.${f.family}`)} ({(f.counts.new ?? 0) + (f.counts.different ?? 0)})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <nav className="hidden lg:block" aria-label={t('sync.family_select')}>
        <ul className="space-y-0.5 rounded-lg border p-1">
          {families.map((f) => {
            const newCount = f.counts.new ?? 0
            const diffCount = f.counts.different ?? 0
            const actionable = newCount + diffCount > 0
            return (
              <li key={f.family} className={cn('flex items-center gap-2 rounded-md px-2', f.family === current && 'bg-muted')}>
                {canWrite && (
                  <TriStateCheckbox
                    state={familyCheckState(f, selection)}
                    disabled={!actionable}
                    onChange={(checked) => onToggleFamily(f, checked)}
                    label={t('sync.family_checkbox', { family: t(`sync.families.${f.family}`) })}
                    testId={`ontology-sync-family-checkbox-${f.family}`}
                  />
                )}
                <button
                  type="button"
                  className={cn(
                    'flex min-w-0 flex-1 items-center justify-between gap-2 py-1.5 text-left text-sm',
                    !actionable && 'text-muted-foreground',
                  )}
                  onClick={() => onFamily(f.family)}
                  aria-current={f.family === current ? 'true' : undefined}
                  data-testid={`ontology-sync-family-${f.family}`}
                  data-new={newCount}
                  data-different={diffCount}
                >
                  <span className="truncate">{t(`sync.families.${f.family}`)}</span>
                  <span className="flex shrink-0 gap-1 text-xs">
                    {newCount > 0 && <Badge variant="success">{newCount}</Badge>}
                    {diffCount > 0 && <Badge variant="warning">{diffCount}</Badge>}
                  </span>
                </button>
              </li>
            )
          })}
        </ul>
      </nav>
    </>
  )
}

function FamilyPanel({
  familyDiff,
  sourceKind,
  statuses,
  q,
  selection,
  canWrite,
  locale,
  onQuery,
  onStatuses,
  onToggle,
  labelOf,
}: {
  familyDiff: SyncFamilyDiff
  sourceKind: SyncSourceKind
  statuses: SyncStatus[]
  q: string
  selection: Selection
  canWrite: boolean
  locale: string
  onQuery: (q: string) => void
  onStatuses: (s: SyncStatus[]) => void
  onToggle: (id: string, checked: boolean) => void
  labelOf: (id: string) => string
}) {
  const { t } = useTranslation('types')
  const [draft, setDraft] = useState(q)
  useEffect(() => setDraft(q), [q])
  useEffect(() => {
    if (draft === q) return
    const h = setTimeout(() => onQuery(draft), 250)
    return () => clearTimeout(h)
  }, [draft, q, onQuery])

  const visible = useMemo(() => familyDiff.items.filter((it) => matchesQuery(it, q)), [familyDiff, q])
  const groups = SYNC_STATUSES.filter((s) => statuses.includes(s))
  const shownCount = visible.filter((it) => statuses.includes(it.status)).length

  return (
    <section className="min-w-0 space-y-3" data-testid={`ontology-sync-panel-${familyDiff.family}`}>
      <div className="flex flex-wrap items-center gap-2">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={t('sync.search_placeholder')}
          aria-label={t('sync.search_aria')}
          className="max-w-xs"
          data-testid="ontology-sync-search"
        />
        <div className="flex flex-wrap gap-1">
          {SYNC_STATUSES.filter((s) => s !== 'unsyncable' || (familyDiff.counts.unsyncable ?? 0) > 0).map((s) => {
            const on = statuses.includes(s)
            return (
              <Button
                key={s}
                size="sm"
                variant={on ? 'secondary' : 'ghost'}
                aria-pressed={on}
                onClick={() => {
                  const next = on ? statuses.filter((x) => x !== s) : [...statuses, s]
                  onStatuses(next.length === 0 ? [...SYNC_STATUSES] : next)
                }}
                data-testid={`ontology-sync-status-${s}`}
              >
                {t(`sync.status.${s}`)} ({familyDiff.counts[s] ?? 0})
              </Button>
            )
          })}
        </div>
      </div>

      {familyDiff.items.length === 0 ? (
        <EmptyState variant="inline" title={t('sync.family_empty')} data-testid="ontology-sync-family-empty" />
      ) : shownCount === 0 ? (
        <EmptyState variant="inline" title={t('sync.no_match')} live data-testid="ontology-sync-no-match" />
      ) : (
        groups.map((status) => {
          const items = visible.filter((it) => it.status === status)
          if (items.length === 0) return null
          return (
            <StatusGroup
              key={`${familyDiff.family}-${status}`}
              status={status}
              sourceKind={sourceKind}
              count={items.length}
              defaultOpen={status === 'new' || status === 'different' || status === 'unsyncable'}
            >
              {items.map((item) => {
                const id = refId(familyDiff.family, item.key)
                const pulledBy = selection.get(id)
                return (
                  <SyncItemRow
                    key={id}
                    family={familyDiff.family}
                    item={item}
                    sourceKind={sourceKind}
                    locale={locale}
                    selected={selection.has(id)}
                    requiredBy={pulledBy ? labelOf(pulledBy) : null}
                    canWrite={canWrite}
                    onToggle={(checked) => onToggle(id, checked)}
                  />
                )
              })}
            </StatusGroup>
          )
        })
      )}
    </section>
  )
}

function StatusGroup({
  status,
  sourceKind,
  count,
  defaultOpen,
  children,
}: {
  status: SyncStatus
  sourceKind: SyncSourceKind
  count: number
  defaultOpen: boolean
  children: React.ReactNode
}) {
  const { t } = useTranslation('types')
  const [open, setOpen] = useState(defaultOpen)
  const title =
    status === 'new' || status === 'different' ? t(`sync.groups.${status}_${sourceKind}`) : t(`sync.groups.${status}`)
  return (
    <div className="rounded-lg border" data-testid={`ontology-sync-group-${status}`}>
      <button
        type="button"
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm font-medium"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        data-testid={`ontology-sync-group-toggle-${status}`}
      >
        <AppIcon path={open ? mdiChevronDown : mdiChevronRight} size="sm" />
        <span>{title}</span>
        <Badge variant={STATUS_BADGE[status]}>{count}</Badge>
        {status === 'local_only' && <span className="text-xs font-normal text-muted-foreground">{t('sync.kept_hint')}</span>}
      </button>
      {open && <ul className="divide-y border-t">{children}</ul>}
    </div>
  )
}

function SyncItemRow({
  family,
  item,
  sourceKind,
  locale,
  selected,
  requiredBy,
  canWrite,
  onToggle,
}: {
  family: SyncFamily
  item: SyncDiffItem
  sourceKind: SyncSourceKind
  locale: string
  selected: boolean
  requiredBy: string | null
  canWrite: boolean
  onToggle: (checked: boolean) => void
}) {
  const { t } = useTranslation('types')
  const [open, setOpen] = useState(false)
  const label = localizedLabel(item.label, locale) || item.key
  const selectable = isSelectable(item)
  const hasDetail = item.status === 'new' || item.status === 'different'
  const sourceSide = sourceKind === 'template' ? t('sync.side_template') : t('sync.side_defaults')
  const typeChange = item.flags?.includes('type_change')
  const tuned = isTuned(item)
  const tuning = useContext(TuningContext)
  const id = refId(family, item.key)

  return (
    <li className="px-3 py-2" data-testid={`ontology-sync-row-${family}-${item.key}`} data-status={item.status}>
      <div className="flex items-start gap-2">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
          {canWrite && selectable && (
            <input
              type="checkbox"
              className="h-4 w-4 rounded border border-border text-primary focus:ring-2 focus:ring-ring focus:ring-offset-2"
              checked={selected}
              onChange={(e) => onToggle(e.target.checked)}
              aria-label={t('sync.select_item', { label })}
              data-testid={`ontology-sync-row-checkbox-${family}-${item.key}`}
            />
          )}
          <span className="min-w-0 truncate text-sm font-medium">{label}</span>
          <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs text-muted-foreground">{item.key}</code>
          <Badge variant={STATUS_BADGE[item.status]}>{t(`sync.status.${item.status}`)}</Badge>
          {requiredBy && (
            <Badge variant="info" data-testid={`ontology-sync-required-by-${family}-${item.key}`}>
              {t('sync.required_by', { label: requiredBy })}
            </Badge>
          )}
          {typeChange && (
            <Badge variant="warning" title={t('sync.type_change_hint')} data-testid={`ontology-sync-type-change-${family}-${item.key}`}>
              {t('sync.type_change')}
            </Badge>
          )}
          {tuned && (
            <Badge variant="info" data-testid={`ontology-sync-tuned-${family}-${item.key}`}>
              {t('sync.tuned')}
            </Badge>
          )}
          {tuned && canWrite && selected && (
            <label className="flex items-center gap-1 text-xs text-muted-foreground">
              <input
                type="checkbox"
                className="h-3.5 w-3.5"
                checked={tuning.reset.has(id)}
                onChange={(e) => tuning.toggle(id, e.target.checked)}
                data-testid={`ontology-sync-reset-tuning-${family}-${item.key}`}
              />
              {t('sync.reset_tuning')}
            </label>
          )}
        </div>
        {hasDetail && (
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0"
            onClick={() => setOpen((o) => !o)}
            aria-expanded={open}
            aria-label={open ? t('sync.hide_changes') : t('sync.show_changes')}
            title={open ? t('sync.hide_changes') : t('sync.show_changes')}
            data-testid={`ontology-sync-row-toggle-${family}-${item.key}`}
          >
            <AppIcon path={open ? mdiChevronDown : mdiChevronRight} size="sm" />
          </Button>
        )}
      </div>
      {open && hasDetail && (
        <div className="mt-2 rounded-md bg-muted/30 p-2">
          {typeChange && <p className="mb-2 text-xs text-warning">{t('sync.type_change_hint')}</p>}
          {item.status === 'new' ? (
            <FieldDiff
              changes={sourceAsChanges(item.source as Record<string, unknown> | undefined)}
              afterLabel={sourceSide}
              data-testid={`ontology-sync-field-diff-${family}-${item.key}`}
            />
          ) : (
            <FieldDiff
              changes={item.changes ?? []}
              beforeLabel={t('sync.this_org')}
              afterLabel={sourceSide}
              data-testid={`ontology-sync-field-diff-${family}-${item.key}`}
            />
          )}
        </div>
      )}
    </li>
  )
}

function ReviewDialog({
  review,
  sourceKind,
  sourceName,
  applying,
  applySeconds,
  applyError,
  locale,
  reextract,
  onReextractChange,
  onCancel,
  onApply,
}: {
  review: ReviewState
  sourceKind: SyncSourceKind
  sourceName: string
  applying: boolean
  applySeconds: number
  applyError: string | null
  locale: string
  reextract: boolean
  onReextractChange: (checked: boolean) => void
  onCancel: () => void
  onApply: () => void
}) {
  const { t } = useTranslation('types')
  const open = review.phase !== 'closed'
  const count = review.phase === 'closed' ? 0 : review.count
  const sourceSide = sourceKind === 'template' ? t('sync.side_template') : t('sync.side_defaults')

  const perFamily = useMemo(() => {
    if (review.phase !== 'ready') return []
    const map = new Map<string, { created: number; updated: number; unchanged: number }>()
    for (const it of review.outcome.items) {
      const row = map.get(it.family) ?? { created: 0, updated: 0, unchanged: 0 }
      if (it.action === 'created') row.created++
      else if (it.action === 'updated') row.updated++
      else row.unchanged++
      map.set(it.family, row)
    }
    return [...map.entries()]
  }, [review])

  return (
    <AlertDialog open={open} onOpenChange={(o) => !o && !applying && onCancel()}>
      <AlertDialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto" data-testid="ontology-sync-review-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>{t('sync.review.title')}</AlertDialogTitle>
          <AlertDialogDescription>{t('sync.review.description', { count, source: sourceName })}</AlertDialogDescription>
        </AlertDialogHeader>

        {review.phase === 'loading' && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="ontology-sync-review-loading">
            <AppIcon path={mdiLoading} size="md" className="motion-safe:animate-spin" />
            {t('sync.review.loading')}
          </div>
        )}

        {review.phase === 'error' && (
          <Alert variant="destructive" data-testid="ontology-sync-review-error">
            <AppIcon path={mdiAlert} size="md" />
            <AlertTitle>{review.error.title || t('sync.review.error')}</AlertTitle>
            <AlertDescription>
              {review.error.missing && review.error.missing.length > 0 && (
                <>
                  <p>{t('sync.review.missing')}</p>
                  <ul className="mt-1 list-disc pl-5">
                    {review.error.missing.map((m) => (
                      <li key={`${m.family}-${m.key}`}>
                        {localizedLabel(m.label, locale) || m.key} <code className="text-xs">{m.key}</code>
                      </li>
                    ))}
                  </ul>
                </>
              )}
              {review.error.errors && review.error.errors.length > 0 && (
                <>
                  <p>{t('sync.review.import_failed')}</p>
                  <ul className="mt-1 list-disc pl-5">
                    {review.error.errors.map((e, i) => (
                      <li key={i}>{e.message}</li>
                    ))}
                  </ul>
                </>
              )}
            </AlertDescription>
          </Alert>
        )}

        {review.phase === 'ready' && (
          <div className="space-y-4 text-sm" data-testid="ontology-sync-review-outcome">
            <div>
              <h3 className="mb-1 font-medium">{t('sync.review.counts_title')}</h3>
              <table className="w-full text-left" data-testid="ontology-sync-review-counts">
                <thead className="text-xs text-muted-foreground">
                  <tr>
                    <th className="py-1 font-medium">{t('sync.review.family')}</th>
                    <th className="py-1 text-right font-medium">{t('sync.review.created')}</th>
                    <th className="py-1 text-right font-medium">{t('sync.review.updated')}</th>
                    <th className="py-1 text-right font-medium">{t('sync.review.unchanged')}</th>
                  </tr>
                </thead>
                <tbody>
                  {perFamily.map(([family, c]) => (
                    <tr key={family} className="border-t" data-testid={`ontology-sync-review-family-${family}`}>
                      <td className="py-1">{t(`sync.families.${family}`)}</td>
                      <td className="py-1 text-right">{c.created}</td>
                      <td className="py-1 text-right">{c.updated}</td>
                      <td className="py-1 text-right">{c.unchanged}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {review.outcome.warnings.length > 0 && (
              <div data-testid="ontology-sync-review-warnings">
                <h3 className="mb-1 font-medium">{t('sync.review.warnings')}</h3>
                <ul className="list-disc space-y-0.5 pl-5 text-xs text-muted-foreground">
                  {review.outcome.warnings.map((w, i) => (
                    <li key={i}>{w}</li>
                  ))}
                </ul>
              </div>
            )}

            {review.outcome.items.some((it) => !it.converged) && (
              <div data-testid="ontology-sync-review-residual">
                <h3 className="mb-1 font-medium">
                  {sourceKind === 'template' ? t('sync.review.residual') : t('sync.review.residual_defaults')}
                </h3>
                <ul className="space-y-2">
                  {review.outcome.items
                    .filter((it) => !it.converged)
                    .map((it) => (
                      <li key={`${it.family}-${it.key}`}>
                        <p className="text-xs font-medium">
                          {localizedLabel(it.label, locale) || it.key} <code className="text-muted-foreground">{it.key}</code>
                        </p>
                        <FieldDiff changes={it.residual ?? []} beforeLabel={t('sync.after')} afterLabel={sourceSide} />
                      </li>
                    ))}
                </ul>
              </div>
            )}

            {review.outcome.collateral.length > 0 && (
              <div data-testid="ontology-sync-review-collateral">
                <h3 className="mb-1 font-medium">{t('sync.review.collateral')}</h3>
                <ul className="space-y-2">
                  {review.outcome.collateral.map((it) => (
                    <li key={`${it.family}-${it.key}`}>
                      <p className="text-xs font-medium">
                        {t(`sync.families.${it.family}`)} · {localizedLabel(it.label, locale) || it.key}
                      </p>
                      <FieldDiff changes={it.changes} beforeLabel={t('sync.before')} afterLabel={t('sync.after')} />
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {reextractionDocuments(review.outcome.reextraction) > 0 && (
              <div data-testid="ontology-sync-review-reextraction">
                <h3 className="mb-1 font-medium">{t('sync.review.reextract_title')}</h3>
                <p className="mb-1 text-xs text-muted-foreground">{t('sync.review.reextract_hint')}</p>
                <ul className="mb-2 space-y-0.5">
                  {(review.outcome.reextraction ?? [])
                    .filter((r) => r.documents > 0)
                    .map((r) => (
                      <li key={r.document_type} data-testid={`ontology-sync-reextraction-${r.document_type}`}>
                        {localizedLabel(r.label, locale) || r.document_type}{' '}
                        <code className="text-xs text-muted-foreground">{r.keys.join(', ')}</code>
                        {' · '}
                        {t('sync.review.reextract_row', { count: r.documents })}
                      </li>
                    ))}
                </ul>
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={reextract}
                    onCheckedChange={onReextractChange}
                    disabled={applying}
                    data-testid="ontology-sync-reextract-checkbox"
                  />
                  {t('sync.review.reextract_checkbox', { count: reextractionDocuments(review.outcome.reextraction) })}
                </label>
              </div>
            )}

            <p className="rounded-md bg-muted/50 p-2 text-xs" data-testid="ontology-sync-review-fixed-line">
              {sourceKind === 'template' ? t('sync.review.fixed_line') : t('sync.review.fixed_line_defaults')}
            </p>
          </div>
        )}

        {applyError && (
          <Alert variant="destructive" data-testid="ontology-sync-apply-error">
            <AlertDescription>{applyError}</AlertDescription>
          </Alert>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel disabled={applying} data-testid="ontology-sync-review-cancel">
            {t('sync.review.cancel')}
          </AlertDialogCancel>
          <Button onClick={onApply} disabled={review.phase !== 'ready' || applying} data-testid="ontology-sync-apply-btn">
            {applying ? (
              <>
                <AppIcon path={mdiLoading} size="sm" className="mr-2 motion-safe:animate-spin" />
                <span data-testid="ontology-sync-apply-progress">
                  {applySeconds > 0 ? t('sync.review.applying_seconds', { seconds: applySeconds }) : t('sync.review.applying')}
                </span>
              </>
            ) : (
              t('sync.review.apply')
            )}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

// invalidateOntologyQueries refreshes the lists built from the ontology the
// sync just rewrote.
function invalidateOntologyQueries(queryClient: ReturnType<typeof useQueryClient>, org: string) {
  queryClient.invalidateQueries({
    predicate: (query) => {
      const key = query.queryKey as unknown[]
      if (!Array.isArray(key)) return false
      // Hand-written keys: ['assetTypes', org], ['documentTypes', org], …
      if (typeof key[0] === 'string' && key.includes(org) && key[0] !== 'get') return true
      if (key[0] !== 'get' || typeof key[1] !== 'string') return false
      const opts = key.find((item) => item && typeof item === 'object' && 'params' in (item as object)) as
        | { params?: { path?: { orgCode?: string } } }
        | undefined
      return opts?.params?.path?.orgCode === org
    },
  })
}
